import { z } from 'zod';
import { changesSchema } from './changes.js';
import { idSchema } from './common.js';
import { lessonIdSchema } from './content.js';
import { contentReviewIdSchema, severitySchema } from './generation.js';
import { citedRefSchema } from './verification.js';
/**
 * A fix asked for one finding on a draft lesson, applied, re-checked and revertible —
 * CURR-36. `docs/decisions/lesson-fixes.md`.
 */
export const lessonFixIdSchema = idSchema
    .describe('A lesson fix id.')
    .brand();
export const requestFixSchema = z
    .strictObject({
    findingId: contentReviewIdSchema
        .optional()
        .describe('A stored finding on this lesson or one of its scenes.'),
    source: z
        .strictObject({
        url: z.string().min(1).describe('The cited URL the check read.'),
        claim: z.string().min(1).max(300),
    })
        .optional()
        .describe('A claim a source check left unsupported or unresolved.'),
})
    .refine((body) => (body.findingId === undefined) !== (body.source === undefined), { message: 'Name a finding or a source claim, not both.' })
    .describe('The finding to fix: a stored one, or a source claim.');
export const lessonFixStatusSchema = z
    .enum([
    'PROPOSED',
    'FAILED',
    'RESOLVED',
    'UNRESOLVED',
    'REVERTED',
    'DISCARDED',
])
    .describe('Waiting for Apply; no proposal could be made; applied and the re-check passed; applied and the re-check did not pass; applied then put back; turned down.');
export const recheckSchema = z
    .enum(['REVIEWER', 'CLAIMS', 'LABELS', 'RUBRIC', 'RULES', 'SOURCES'])
    .describe('A check that raises findings, run again after Apply.');
export const fixedFindingSchema = z
    .strictObject({
    kind: z.enum(['LESSON', 'SCENE', 'SOURCE']),
    findingId: z
        .string()
        .min(1)
        .optional()
        .describe('The stored finding it answers. Absent for a source claim.'),
    severity: severitySchema,
    finding: z.string().min(1),
    suggestion: z.string().min(1),
    position: z
        .number()
        .int()
        .min(1)
        .optional()
        .describe('The scene it names, 1-based, before the fix.'),
    url: z.string().min(1).optional(),
    claim: z.string().min(1).optional(),
})
    .describe('The finding as it was asked about.');
export const sceneChangeSchema = z
    .strictObject({
    position: z.number().int().min(1).describe('Its place after the fix.'),
    from: z
        .number()
        .int()
        .min(1)
        .optional()
        .describe('Its place before. Absent for an added scene.'),
    change: z.enum(['UNCHANGED', 'REWRITTEN', 'ADDED']),
    changes: changesSchema.shape.changes.optional(),
    source: z
        .strictObject({
        before: z.string().nullable(),
        after: z.string().nullable(),
    })
        .optional()
        .describe('The URL the scene cites, when the fix changes it.'),
})
    .describe('One scene of the lesson after the fix.');
export const lessonFixSchema = z
    .strictObject({
    id: lessonFixIdSchema,
    lessonId: lessonIdSchema,
    status: lessonFixStatusSchema,
    finding: fixedFindingSchema,
    why: z
        .string()
        .min(1)
        .optional()
        .describe('Why the change answers the finding. Absent on a failure.'),
    title: z.strictObject({ before: z.string(), after: z.string() }).optional(),
    summary: z
        .strictObject({ before: z.string(), after: z.string() })
        .optional(),
    scenes: z.array(sceneChangeSchema),
    removed: z
        .array(z.number().int().min(1))
        .describe('Places before the fix of the scenes it drops.'),
    sources: z.strictObject({
        added: z.array(citedRefSchema),
        removed: z.array(citedRefSchema),
    }),
    claim: z
        .string()
        .min(1)
        .nullable()
        .optional()
        .describe('A source fix’s claim as the lesson makes it after the fix, null when it is cut.'),
    rechecked: z
        .array(recheckSchema)
        .optional()
        .describe('The checks Apply ran again. Absent until applied.'),
    remaining: z
        .array(z.string().min(1))
        .describe('What still stands: the re-check’s findings, or why none ran.'),
    nextStep: z
        .string()
        .min(1)
        .optional()
        .describe('What a person does when the fix did not resolve the finding.'),
    createdAt: z.iso.datetime(),
    appliedAt: z.iso.datetime().optional(),
    revertedAt: z.iso.datetime().optional(),
})
    .describe('One fix, from proposal to re-check.');
export const lessonFixesSchema = z
    .strictObject({ fixes: z.array(lessonFixSchema).describe('Newest first.') })
    .describe('A lesson’s fixes.');
//# sourceMappingURL=lesson-fix.js.map